import React from 'react';
import { fmtDateTime } from '@shared/formatDate';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Link,
} from '@mui/material';
import { PromoTransaction } from '../../services/accountService';

interface PromoTransactionDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: PromoTransaction | null;
  onOpenLinkedTransaction?: (transactionId: string) => void;
}

const PromoTransactionDetailsDialog: React.FC<PromoTransactionDetailsDialogProps> = ({ 
  open, 
  onClose, 
  transaction,
  onOpenLinkedTransaction,
}) => {
  if (!transaction) return null;

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );

  const isDebit = transaction.transactionType === 'SPEND' || transaction.transactionType === 'EXPIRE';
  const isSpend = transaction.transactionType === 'SPEND';
  const vendorCreditsUsed = Math.abs(transaction.vendorCreditsUsed ?? (isSpend ? transaction.amount : 0));
  const regularCreditsUsed = Math.abs(transaction.regularCreditsUsed ?? 0);
  const purchasePrice = vendorCreditsUsed + regularCreditsUsed;
  const linkedPurchaseId = transaction.referenceId || transaction.linkedRegularTransactionId;

  const renderSignedCurrency = (value: number, negative: boolean) => {
    const abs = Math.abs(value);
    const sign = abs > 0 ? (negative ? '-' : '+') : '';
    const color = abs === 0 ? 'text.primary' : negative ? 'error.main' : 'success.main';
    const fontWeight = abs === 0 ? 400 : 600;
    return (
      <Typography component="span" variant="body2" sx={{ color, fontWeight }}>
        {`${sign}$${abs.toFixed(4)}`}
      </Typography>
    );
  };
  
  // Parse the description to extract product name
  // Remove prefixes like "Product purchase:", "Product sale:", "Purchase:"
  const parseProductName = (desc: string | undefined): string => {
    if (!desc) return '';
    return desc
      .replace(/^Product purchase:\s*/i, '')
      .replace(/^Product sale:\s*/i, '')
      .replace(/^Purchase:\s*/i, '')
      .trim();
  };
  
  const productName = parseProductName(transaction.description);
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #56903a 0%, #64903a 100%)', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        🎁 Vendor Credit Transaction
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          {/* For SPEND transactions, show simplified format */}
          {isSpend && productName && (
            <DetailRow label="Product" value={productName} />
          )}
          
          {/* For non-SPEND transactions with description, show it */}
          {!isSpend && transaction.description && (
            <DetailRow label="Description" value={transaction.description} />
          )}

          {/* Vendor Credit Used (for SPEND) or Amount (for others) */}
          <DetailRow 
            label={isSpend ? "Vendor Credit Used" : "Amount"} 
            value={renderSignedCurrency(transaction.amount, isDebit)}
          />

          {/* Date & Time */}
          <DetailRow 
            label="Date & Time" 
            value={fmtDateTime(transaction.transactionDate)} 
          />

          {/* Transaction ID */}
          <DetailRow 
            label="Transaction ID" 
            value={
              <Typography 
                variant="body2" 
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              >
                {transaction.transactionId}
              </Typography>
            } 
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            Sponsor & Campaign
          </Typography>
          
          <DetailRow label="Sponsor" value={transaction.sponsorUsername} />
          <DetailRow label="Campaign" value={transaction.campaignName} />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            Split Funding
          </Typography>
          
          <DetailRow 
            label="Balance Before" 
            value={`$${transaction.balanceBefore.toFixed(4)}`} 
          />
          <DetailRow 
            label="From Vendor Credits"
            value={renderSignedCurrency(vendorCreditsUsed, true)}
          />
          <DetailRow
            label="From 2Way Credits"
            value={renderSignedCurrency(regularCreditsUsed, true)}
          />
          <DetailRow
            label="Split Source"
            value={transaction.splitSource === 'template-fallback' ? 'Fallback (template/message)' : 'Ledger linked'}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            Purchase & Source
          </Typography>
          <DetailRow label="Purchase Price" value={`$${purchasePrice.toFixed(4)}`} />
          <DetailRow
            label="Source URL"
            value={
              linkedPurchaseId && onOpenLinkedTransaction ? (
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenLinkedTransaction(linkedPurchaseId);
                  }}
                  sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                >
                  Open in linked purchase details
                </Link>
              ) : 'N/A'
            }
          />
          {transaction.linkedRegularTransactionId && (
            <DetailRow
              label="2Way Credit Details"
              value={
                onOpenLinkedTransaction ? (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenLinkedTransaction(transaction.linkedRegularTransactionId!);
                    }}
                    sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                  >
                    View linked transaction
                  </Link>
                ) : (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {transaction.linkedRegularTransactionId}
                  </Typography>
                )
              }
            />
          )}
          <DetailRow 
            label={isDebit ? "Difference" : "Amount Added"} 
            value={renderSignedCurrency(transaction.amount, isDebit)}
          />
          <DetailRow 
            label="Balance After" 
            value={
              <span style={{ fontWeight: 'bold' }}>
                ${transaction.balanceAfter.toFixed(4)}
              </span>
            } 
          />

          {(transaction.referenceId || transaction.referenceType) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                Reference
              </Typography>
              
              {transaction.referenceType && (
                <DetailRow label="Reference Type" value={transaction.referenceType} />
              )}
              {transaction.referenceId && (
                <DetailRow 
                  label="Reference ID" 
                  value={
                    <Typography 
                      variant="body2" 
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    >
                      {transaction.referenceId}
                    </Typography>
                  } 
                />
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ 
            p: 2, 
            borderRadius: 1, 
            bgcolor: 'info.50',
            border: 1,
            borderColor: 'info.200'
          }}>
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Note:</strong> Vendor credits from <strong>{transaction.sponsorUsername}</strong> can 
              only be used with this specific vendor and may expire. They cannot be transferred or converted 
              to 2Way Credits. 2Way Credits can be used anywhere and never expire.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ 
          background: 'linear-gradient(135deg, #56903a 0%, #64903a 100%)'
        }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoTransactionDetailsDialog;
